import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    collection, doc, getDocs, onSnapshot, updateDoc, deleteDoc, setDoc, serverTimestamp
} from 'firebase/firestore';
import {
    getDb, authApi, authApiIdempotent, initFirebase, onAuth, getAuthInstance, waitForUserAvailable, waitForDbAvailable
} from '../firebase/config.js';
import { ENV } from '../config/env.js';
import { dueCount, isDue } from '../utils/srs.js';
import { addXP, recordStudyToday, XP_PER_CARD } from '../components/PerformanceDashboard.jsx';
import EditableCardContent from '../components/EditableCardContent.jsx';
import FlashcardViewer from '../components/FlashcardViewer.jsx';
import GamesModal from '../components/GamesModal.jsx';
import GameViewer from '../components/GameViewer.jsx';
import NemesisGame from '../components/NemesisGame.jsx';
import FlashNotesActionModal from '../components/FlashNotesActionModal.jsx';
import FlashNotesViewer from '../components/FlashNotesViewer.jsx';
import StudyRecapModal from '../components/StudyRecapModal.jsx';
import CreditConversionModal from '../components/CreditConversionModal.jsx';
import { exportFolderToAnki } from '../utils/ankiExport.js';
import { parseAnkiFile } from '../utils/ankiImport.js';
import FolderChatSidebar from '../components/FolderChatSidebar.jsx';
import ClinicalDocumentStudyModal from '../components/clinical/ClinicalDocumentStudyModal.jsx';
import { DEFAULT_CLINICAL_SETTINGS } from '../components/clinical/clinicalOptions.js';
import { cacheFolderList, cacheFolderCards, getCachedFolderList, getCachedFolderCards, getCachedImageUrl } from '../utils/offlineCache.js';
import { useOnlineStatus } from '../hooks/useOnlineStatus.js';
import { isOfficeFile, convertOfficeFileToPdf } from '../utils/officeConversion.js';

const API_BASE_URL = ENV.apiBaseUrl;

// ── Helpers ──────────────────────────────────────────────────────────────────

const getUid = () => {
    try {
        const auth = getAuthInstance();
        return auth?.currentUser?.uid ?? null;
    } catch {
        return null;
    }
};

const timeAgo = (ms) => {
    if (!ms) return null;
    const diff = Date.now() - ms;
    const days = Math.floor(diff / 86400000);
    if (days === 0) return 'today';
    if (days === 1) return 'yesterday';
    if (days < 30) return `${days}d ago`;
    const months = Math.floor(days / 30);
    return `${months}mo ago`;
};

// ── Mini toast system ─────────────────────────────────────────────────────────

const Toast = ({ toasts }) => (
    <div className="fixed bottom-5 right-5 z-[9999] flex flex-col gap-2 pointer-events-none">
        {toasts.map(t => (
            <div
                key={t.id}
                className={`rounded-xl px-4 py-2.5 text-sm font-medium shadow-lg backdrop-blur text-white ${
                    t.kind === 'error' ? 'bg-rose-600/90' :
                    t.kind === 'success' ? 'bg-emerald-600/90' : 'bg-slate-700/90'
                }`}
            >
                {t.text}
            </div>
        ))}
    </div>
);

// ── Card list item ────────────────────────────────────────────────────────────

const CardItem = ({ card, index, onSave, onDelete, sourceDocuments, onViewSource }) => {
    const sourceDoc = card.sourceDocumentId ? sourceDocuments?.find((d) => d.id === card.sourceDocumentId) : null;
    const [open, setOpen] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [pendingDelete, setPendingDelete] = useState(false);
    const due = isDue(card);

    if (isEditing) {
        return (
            <div className="w-full rounded-xl border border-white/8 bg-slate-900/50 p-4">
                <EditableCardContent
                    card={card}
                    onSave={(updated) => { onSave(updated); setIsEditing(false); }}
                    onCancel={() => setIsEditing(false)}
                />
            </div>
        );
    }

    return (
        <div className="w-full rounded-xl border border-white/8 bg-slate-900/50 hover:border-white/15 hover:bg-slate-900/70 transition group">
            <button
                type="button"
                className="w-full text-left p-4"
                onClick={() => setOpen(v => !v)}
            >
                <div className="flex items-start justify-between gap-3">
                    <div className="flex items-start gap-3 flex-1 min-w-0">
                        <span className="mt-0.5 text-xs text-slate-600 tabular-nums shrink-0 w-6 text-right">{index + 1}</span>
                        <div className="flex-1 min-w-0">
                            <p className="text-sm text-slate-200 leading-snug">{card.question || '—'}</p>
                            {open && (
                                <p className="mt-2 text-sm text-slate-400 leading-snug border-t border-white/8 pt-2">
                                    {card.answer || '—'}
                                </p>
                            )}
                        </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                        {due && (
                            <span className="text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded-full bg-amber-500/15 text-amber-300 ring-1 ring-amber-400/20">
                                Due
                            </span>
                        )}
                        <svg
                            className={`h-4 w-4 text-slate-600 group-hover:text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`}
                            fill="none" viewBox="0 0 24 24" stroke="currentColor"
                        >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                    </div>
                </div>
            </button>
            {open && (
                <div className="flex items-center gap-2 px-4 pb-4 -mt-1">
                    <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); setIsEditing(true); }}
                        className="text-xs rounded-lg px-2.5 py-1 text-slate-300 bg-slate-800/70 ring-1 ring-white/10 hover:bg-slate-800 hover:text-white transition"
                    >
                        Edit
                    </button>
                    {sourceDoc && (
                        <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); onViewSource(sourceDoc, card.sourceDocumentPage); }}
                            className="text-xs rounded-lg px-2.5 py-1 text-teal-300 bg-slate-800/70 ring-1 ring-white/10 hover:bg-teal-500/10 hover:text-teal-200 transition"
                            title={`View source: ${sourceDoc.fileName}`}
                        >
                            📄 View source{card.sourceDocumentPage ? ` (p.${card.sourceDocumentPage})` : ''}
                        </button>
                    )}
                    {!pendingDelete ? (
                        <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); setPendingDelete(true); }}
                            className="text-xs rounded-lg px-2.5 py-1 text-rose-300 bg-slate-800/70 ring-1 ring-white/10 hover:bg-rose-500/10 hover:text-rose-200 transition"
                        >
                            Delete
                        </button>
                    ) : (
                        <span className="inline-flex items-center gap-2 text-xs text-slate-300">
                            Delete this card?
                            <button type="button" onClick={(e) => { e.stopPropagation(); onDelete(card.id); }} className="text-rose-300 hover:text-rose-200 font-medium">Yes</button>
                            <button type="button" onClick={(e) => { e.stopPropagation(); setPendingDelete(false); }} className="text-slate-400 hover:text-white">No</button>
                        </span>
                    )}
                </div>
            )}
        </div>
    );
};

// ── Action button ─────────────────────────────────────────────────────────────

const ActionBtn = ({ icon, label, onClick, primary, disabled, badge }) => (
    <button
        type="button"
        disabled={disabled}
        onClick={onClick}
        className={`relative inline-flex flex-col items-center gap-1.5 rounded-2xl px-5 py-3.5 text-sm font-semibold transition disabled:opacity-40 disabled:cursor-not-allowed select-none ${
            primary
                ? 'bg-gradient-to-r from-teal-600 to-emerald-600 text-white shadow-lg shadow-teal-600/25 hover:opacity-90'
                : 'bg-white/6 border border-white/10 text-slate-200 hover:bg-white/10'
        }`}
    >
        <span className="text-xl leading-none">{icon}</span>
        <span className="text-xs font-medium">{label}</span>
        {badge && (
            <span className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] flex items-center justify-center rounded-full bg-amber-400 text-[9px] font-bold text-slate-900 px-1">
                {badge}
            </span>
        )}
    </button>
);

// ── Main page ─────────────────────────────────────────────────────────────────

const DeckPage = ({ entitlements, isDevMode, refreshEntitlements, onOpenPlanChooser, onOpenAccount }) => {
    const { folderId } = useParams();
    const navigate = useNavigate();

    // Data
    const [folder, setFolder] = useState(null);
    const [cards, setCards] = useState([]);
    const [cardsLoaded, setCardsLoaded] = useState(false);
    const [loading, setLoading] = useState(true);
    const [cardSearch, setCardSearch] = useState('');

    // Overlays
    const [studyingFolder, setStudyingFolder] = useState(null);
    const [showGamesModal, setShowGamesModal] = useState(null);
    const [gameModeFolder, setGameModeFolder] = useState(null);
    const [nemesisModeFolder, setNemesisModeFolder] = useState(null);
    const [mostRecentScore, setMostRecentScore] = useState(null);
    const [flashNotesAction, setFlashNotesAction] = useState(null);
    const [flashNotesContent, setFlashNotesContent] = useState(null);
    const [showFlashNotesViewer, setShowFlashNotesViewer] = useState(false);
    const [isGeneratingNotes, setIsGeneratingNotes] = useState(false);
    const [creditPrompt, setCreditPrompt] = useState(null);
    const [isExportingAnki, setIsExportingAnki] = useState(false);
    const [isImportingAnki, setIsImportingAnki] = useState(false);
    const [showAnkiMenu, setShowAnkiMenu] = useState(false);
    const ankiMenuRef = useRef(null);
    const ankiFileInputRef = useRef(null);
    const [showTutorChat, setShowTutorChat] = useState(false);
    const [sourceDocuments, setSourceDocuments] = useState([]);
    const [sourceDocumentsLoaded, setSourceDocumentsLoaded] = useState(false);
    const [showDocumentsMenu, setShowDocumentsMenu] = useState(false);
    const documentsMenuRef = useRef(null);
    const documentFileInputRef = useRef(null);
    const [documentStudyFile, setDocumentStudyFile] = useState(null); // new upload
    const [studyDocument, setStudyDocument] = useState(null); // reopening a saved document
    const [documentStudyInitialPage, setDocumentStudyInitialPage] = useState(null); // "View source" from a card
    const [showDocumentStudyModal, setShowDocumentStudyModal] = useState(false);
    const [isConvertingDocument, setIsConvertingDocument] = useState(false);

    useEffect(() => {
        const onClickAway = (e) => {
            if (!ankiMenuRef.current) return;
            if (!ankiMenuRef.current.contains(e.target)) setShowAnkiMenu(false);
        };
        if (showAnkiMenu) document.addEventListener('mousedown', onClickAway);
        return () => document.removeEventListener('mousedown', onClickAway);
    }, [showAnkiMenu]);

    useEffect(() => {
        const onClickAway = (e) => {
            if (!documentsMenuRef.current) return;
            if (!documentsMenuRef.current.contains(e.target)) setShowDocumentsMenu(false);
        };
        if (showDocumentsMenu) document.addEventListener('mousedown', onClickAway);
        return () => document.removeEventListener('mousedown', onClickAway);
    }, [showDocumentsMenu]);

    const loadSourceDocuments = useCallback(async () => {
        if (sourceDocumentsLoaded) return;
        const uid = getUid();
        const db = getDb();
        if (!uid || !db) return;
        try {
            const snap = await getDocs(collection(db, 'users', uid, 'folders', folderId, 'sourceDocuments'));
            setSourceDocuments(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
            setSourceDocumentsLoaded(true);
        } catch (_) {
            // Non-critical — the upload/study flow still works without the saved list.
        }
    }, [folderId, sourceDocumentsLoaded]);
    const isOnline = useOnlineStatus();

    // Post-study recap
    const [studyRecap, setStudyRecap] = useState(null); // { reviewed, xpEarned, masteryPct }

    // Toasts
    const [toasts, setToasts] = useState([]);
    const pushToast = useCallback((kind, text) => {
        const id = Date.now();
        setToasts(prev => [...prev, { id, kind, text }]);
        setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 3500);
    }, []);

    // ── Auth + Firestore subscription ─────────────────────────────────────────

    useEffect(() => {
        let unsub = null;
        let unsubFolder = null;
        let cancelled = false;

        const loadFromCacheFallback = async () => {
            const cachedFolders = await getCachedFolderList();
            const cached = cachedFolders.find((f) => f.id === folderId);
            if (cached) setFolder((prev) => ({ ...cached, leaderboard: cached.leaderboard || [], cards: prev?.cards ?? [] }));
            setLoading(false);
        };

        const setup = async () => {
            await initFirebase();

            // A cold/direct load of this route (deep link, hard reload) can mount before
            // Firebase Auth finishes restoring a persisted session from IndexedDB — give
            // it a real chance to resolve (same waitForUserAvailable retry pattern already
            // used for folder loading in AppHome.jsx) instead of registering the listener
            // and treating its first "no user yet" callback as "no user at all".
            if (!getAuthInstance()?.currentUser) {
                try { await waitForUserAvailable(3500); } catch (_) {}
            }
            if (cancelled) return;

            unsub = onAuth(async (user) => {
                if (unsubFolder) { try { unsubFolder(); } catch (_) {} unsubFolder = null; }

                const uid = user?.uid ?? null;
                if (!uid || !folderId) {
                    if (!navigator.onLine) { await loadFromCacheFallback(); return; }
                    setLoading(false);
                    return;
                }

                const db = await waitForDbAvailable(3000);
                if (cancelled) return;
                if (!db) {
                    if (!navigator.onLine) { await loadFromCacheFallback(); return; }
                    setLoading(false);
                    return;
                }

                const folderRef = doc(db, 'users', uid, 'folders', folderId);
                unsubFolder = onSnapshot(folderRef, (snap) => {
                    if (!snap.exists()) {
                        setFolder(null);
                        setLoading(false);
                        return;
                    }
                    const raw = snap.data() || {};
                    const toMs = (v) =>
                        v && typeof v.toMillis === 'function' ? v.toMillis() :
                        typeof v === 'number' ? v : Date.now();
                    const nextFolder = {
                        id: snap.id,
                        name: raw.name || 'Untitled',
                        color: raw.color || 'from-teal-500 to-emerald-600',
                        cardCount: raw.cardCount ?? 0,
                        flashNotes: raw.flashNotes ?? '',
                        createdAt: toMs(raw.createdAt),
                        updatedAt: toMs(raw.updatedAt),
                        leaderboard: raw.leaderboard || [],
                    };
                    setFolder(prev => ({ ...nextFolder, cardCount: raw.cardCount ?? prev?.cardCount ?? 0, cards: prev?.cards ?? [] }));
                    cacheFolderList([nextFolder]);
                    setLoading(false);
                }, async () => {
                    if (!navigator.onLine) await loadFromCacheFallback();
                });
            });
        };

        setup();
        return () => {
            cancelled = true;
            try { unsub?.(); } catch (_) {}
            try { unsubFolder?.(); } catch (_) {}
        };
    }, [folderId]);

    // ── Card loader ───────────────────────────────────────────────────────────

    const loadCards = useCallback(async () => {
        if (cardsLoaded) return cards;
        const uid = getUid();
        const db = getDb();
        if (!navigator.onLine || !uid || !db || !folderId) {
            const cached = await getCachedFolderCards(folderId);
            if (cached.length) {
                setCards(cached);
                setCardsLoaded(true);
                setFolder(prev => prev ? { ...prev, cards: cached, cardCount: cached.length } : prev);
            }
            return cached;
        }
        try {
            const snap = await getDocs(collection(db, 'users', uid, 'folders', folderId, 'cards'));
            const loaded = snap.docs.map(cd => {
                const c = cd.data() || {};
                return {
                    id: cd.id,
                    question: c.question || '',
                    answer: c.answer || '',
                    isFlagged: !!c.isFlagged,
                    imageUrl: c.imageUrl ?? null,
                    clinicalQuestionId: c.clinicalQuestionId ?? null,
                    sourceDocumentId: c.sourceDocumentId ?? null,
                    sourceDocumentPage: c.sourceDocumentPage ?? null,
                    srsInterval: c.srsInterval,
                    srsEaseFactor: c.srsEaseFactor,
                    srsRepetitions: c.srsRepetitions,
                    srsDueDate: c.srsDueDate,
                    lastViewed: c.lastViewed,
                };
            });
            setCards(loaded);
            setCardsLoaded(true);
            setFolder(prev => prev ? { ...prev, cards: loaded, cardCount: loaded.length } : prev);
            cacheFolderCards(folderId, loaded);
            return loaded;
        } catch {
            return getCachedFolderCards(folderId);
        }
    }, [cardsLoaded, cards, folderId]);

    // Load cards in background once folder is ready
    useEffect(() => {
        if (folder && !cardsLoaded) {
            loadCards();
        }
    }, [folder?.id]);

    // ── Card edit / delete ──────────────────────────────────────────────────────

    const handleUpdateCard = useCallback(async (updatedCard) => {
        setCards(prev => prev.map(c => c.id === updatedCard.id ? { ...c, ...updatedCard } : c));
        try {
            const uid = getUid();
            const db = getDb();
            if (uid && db && folderId) {
                await updateDoc(doc(db, 'users', uid, 'folders', folderId, 'cards', String(updatedCard.id)), {
                    question: updatedCard.question,
                    answer: updatedCard.answer,
                });
            }
            pushToast('success', 'Card updated');
        } catch (e) {
            pushToast('error', e?.message || 'Failed to update card');
        }
    }, [folderId, pushToast]);

    const handleDeleteCard = useCallback(async (cardId) => {
        setCards(prev => prev.filter(c => c.id !== cardId));
        try {
            const uid = getUid();
            const db = getDb();
            if (uid && db && folderId) {
                await deleteDoc(doc(db, 'users', uid, 'folders', folderId, 'cards', String(cardId)));
            }
            pushToast('success', 'Card deleted');
        } catch (e) {
            pushToast('error', e?.message || 'Failed to delete card');
        }
    }, [folderId, pushToast]);

    // ── Derived ───────────────────────────────────────────────────────────────

    const dueCards = useMemo(() => cards.filter(c => isDue(c)).length, [cards]);
    const masteryPct = useMemo(() => {
        if (!cards.length) return 0;
        const mastered = cards.filter(c => (c.srsRepetitions ?? 0) >= 3).length;
        return Math.round((mastered / cards.length) * 100);
    }, [cards]);

    const filteredCards = useMemo(() => {
        if (!cardSearch.trim()) return cards;
        const q = cardSearch.toLowerCase();
        return cards.filter(c =>
            c.question.toLowerCase().includes(q) || c.answer.toLowerCase().includes(q)
        );
    }, [cards, cardSearch]);

    // ── SRS analytics ─────────────────────────────────────────────────────────

    const srsStats = useMemo(() => {
        if (!cards.length) return null;
        const newCards     = cards.filter(c => !(c.srsRepetitions));
        const learning     = cards.filter(c => (c.srsRepetitions ?? 0) >= 1 && (c.srsRepetitions ?? 0) < 3);
        const mastered     = cards.filter(c => (c.srsRepetitions ?? 0) >= 3);
        return {
            new:      newCards.length,
            learning: learning.length,
            mastered: mastered.length,
            total:    cards.length,
        };
    }, [cards]);

    const hardestCards = useMemo(() => {
        return [...cards]
            .filter(c => (c.srsRepetitions ?? 0) > 0)
            .sort((a, b) => (a.srsEaseFactor ?? 2.5) - (b.srsEaseFactor ?? 2.5))
            .slice(0, 5);
    }, [cards]);

    // ── Action handlers ───────────────────────────────────────────────────────

    const openStudy = useCallback(async () => {
        const loaded = await loadCards();
        const f = { ...folder, cards: loaded };
        setStudyingFolder(f);
    }, [folder, loadCards]);

    const handleFocusStudy = useCallback(async () => {
        const loaded = await loadCards();
        const focusCards = loaded.filter(c => isDue(c) || (c.srsEaseFactor ?? 2.5) < 2.2);
        if (focusCards.length === 0) {
            pushToast('success', 'No struggling cards right now — great work!');
            return;
        }
        setStudyingFolder({ ...folder, cards: focusCards, _isFocusMode: true });
    }, [folder, loadCards, pushToast]);

    const openGames = useCallback(async () => {
        const loaded = await loadCards();
        const f = { ...folder, cards: loaded };
        setShowGamesModal(f);
    }, [folder, loadCards]);

    const handleLaunchGame = useCallback(async (f) => {
        try {
            if (!isDevMode) {
                await authApi(`${API_BASE_URL}/api/credits/debit`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ feature: 'game', quantity: 1 }),
                });
            }
            if (typeof refreshEntitlements === 'function') {
                try { await refreshEntitlements(); } catch (_) {}
            }
            setShowGamesModal(null);
            setGameModeFolder(f);
        } catch (e) {
            if (e?.code === 'INSUFFICIENT_CREDITS') {
                pushToast('error', e.message || 'Not enough credits');
            } else {
                pushToast('error', e?.message || 'Failed to start game');
            }
        }
    }, [isDevMode, refreshEntitlements, pushToast]);

    const handleLaunchNemesis = useCallback(async (f) => {
        try {
            if (!isDevMode) {
                await authApi(`${API_BASE_URL}/api/credits/debit`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ feature: 'game', quantity: 1 }),
                });
            }
            if (typeof refreshEntitlements === 'function') {
                try { await refreshEntitlements(); } catch (_) {}
            }
            setShowGamesModal(null);
            setNemesisModeFolder(f);
        } catch (e) {
            if (e?.code === 'INSUFFICIENT_CREDITS') {
                pushToast('error', e.message || 'Not enough credits');
            } else {
                pushToast('error', e?.message || 'Failed to start Nemesis');
            }
        }
    }, [isDevMode, refreshEntitlements, pushToast]);

    const handleGameEnd = useCallback((folderId, finalScore, playerName, levelName) => {
        const entry = { id: Date.now(), name: playerName, score: finalScore, date: Date.now(), level: levelName };
        const uid = getUid();
        const db = getDb();
        setFolder(prev => {
            if (!prev) return prev;
            const newBoard = [...(prev.leaderboard || []), entry]
                .sort((a, b) => b.score - a.score || b.date - a.date)
                .slice(0, 10);
            if (uid && db) {
                updateDoc(doc(db, 'users', uid, 'folders', prev.id), {
                    leaderboard: newBoard,
                }).catch(() => {});
            }
            return { ...prev, leaderboard: newBoard };
        });
        setMostRecentScore(entry);
        pushToast('success', `Game finished: ${finalScore} pts`);
    }, [pushToast]);

    const handleExportAnki = useCallback(async () => {
        setIsExportingAnki(true);
        try {
            const loaded = await loadCards();
            await exportFolderToAnki({ ...folder, cards: loaded });
            pushToast('success', 'Anki deck exported');
        } catch (e) {
            pushToast('error', e.message || 'Export Anki failed');
        } finally {
            setIsExportingAnki(false);
        }
    }, [folder, loadCards, pushToast]);

    const handleImportAnkiFile = useCallback(async (file) => {
        setIsImportingAnki(true);
        try {
            const parsed = await parseAnkiFile(file);
            if (parsed.length === 0) throw new Error('No cards found in this .apkg file.');
            const uid = getUid();
            const db = getDb();
            if (!uid || !db) throw new Error('Not signed in');
            const newCards = await Promise.all(parsed.map(async ({ question, answer }) => {
                const cardRef = doc(collection(db, 'users', uid, 'folders', folderId, 'cards'));
                await setDoc(cardRef, {
                    question, answer, confidence: 'again', isFlagged: false,
                    createdAt: serverTimestamp(), updatedAt: serverTimestamp(),
                });
                return { id: cardRef.id, question, answer, isFlagged: false, imageUrl: null, clinicalQuestionId: null };
            }));
            const newCount = (folder?.cardCount ?? cards.length) + newCards.length;
            setCards(prev => [...prev, ...newCards]);
            setFolder(prev => prev ? { ...prev, cardCount: newCount } : prev);
            updateDoc(doc(db, 'users', uid, 'folders', folderId), { cardCount: newCount, updatedAt: serverTimestamp() }).catch(() => {});
            pushToast('success', `Imported ${newCards.length} cards from Anki`);
        } catch (e) {
            pushToast('error', e.message || 'Anki import failed');
        } finally {
            setIsImportingAnki(false);
        }
    }, [folder, cards.length, folderId, pushToast]);

    const handleNewDocumentFile = useCallback((file) => {
        if (!file) return;
        setStudyDocument(null);
        setDocumentStudyInitialPage(null);
        // Word/PPT/Excel: convert to PDF server-side (LibreOffice) first, then open the same
        // study modal PDFs use directly — matches AppHome's upload flow.
        if (isOfficeFile(file)) {
            setIsConvertingDocument(true);
            pushToast('info', 'Converting document — this can take a bit for larger files...');
            convertOfficeFileToPdf(file)
                .then((pdfFile) => {
                    setDocumentStudyFile(pdfFile);
                    setShowDocumentStudyModal(true);
                })
                .catch((err) => {
                    pushToast('error', err?.message || 'Failed to convert document — try again.');
                })
                .finally(() => setIsConvertingDocument(false));
            return;
        }
        setDocumentStudyFile(file);
        setShowDocumentStudyModal(true);
    }, [pushToast]);

    const handleReopenDocument = useCallback((docMeta) => {
        setDocumentStudyFile(null);
        setStudyDocument(docMeta);
        setDocumentStudyInitialPage(null);
        setShowDocumentStudyModal(true);
    }, []);

    // "View source" from a card — same reopen flow, but jumps straight to the page the card
    // was generated from instead of leaving the viewer at page 1.
    const handleViewCardSource = useCallback((docMeta, page) => {
        setDocumentStudyFile(null);
        setStudyDocument(docMeta);
        setDocumentStudyInitialPage(page || null);
        setShowDocumentStudyModal(true);
    }, []);

    // Credits are spent server-side per in-modal generation, same as the Anki import /
    // clinical batch-generate flows — refresh entitlements immediately rather than waiting
    // for the modal to close so the credit counter elsewhere in the app stays accurate.
    const handleDocumentModalCardsGenerated = useCallback(async () => {
        if (typeof refreshEntitlements === 'function') { try { await refreshEntitlements(); } catch (_) {} }
    }, [refreshEntitlements]);

    // The modal itself now writes cards straight into the folder (same pattern as Anki
    // import) and updates the folder's cardCount doc directly, so this just needs to refresh
    // DeckPage's own local card list to reflect what the modal already saved, plus the
    // source-documents list in case a new document was saved for reuse.
    const handleDocumentModalDone = useCallback(async ({ folderId: savedFolderId, count } = {}) => {
        setShowDocumentStudyModal(false);
        setDocumentStudyFile(null);
        setStudyDocument(null);
        setSourceDocumentsLoaded(false);
        loadSourceDocuments();
        if (!count || count === 0) return;
        const uid = getUid();
        const db = getDb();
        if (!uid || !db) return;
        try {
            const snap = await getDocs(collection(db, 'users', uid, 'folders', folderId, 'cards'));
            const refreshed = snap.docs.map((d) => {
                const c = d.data() || {};
                return { id: d.id, question: c.question || '', answer: c.answer || '', isFlagged: !!c.isFlagged, imageUrl: c.imageUrl ?? null, clinicalQuestionId: c.clinicalQuestionId ?? null, sourceDocumentId: c.sourceDocumentId ?? null, sourceDocumentPage: c.sourceDocumentPage ?? null };
            });
            setCards(refreshed);
            setFolder(prev => prev ? { ...prev, cardCount: refreshed.length } : prev);
            pushToast('success', `${count} flashcard${count === 1 ? '' : 's'} added`);
        } catch (e) {
            pushToast('error', e.message || 'Saved, but failed to refresh the card list — reload to see them.');
        }
    }, [folderId, pushToast, loadSourceDocuments]);

    // Previously only reachable buried inside Study mode's settings submenu — a student
    // confused about a subject browsing the deck page itself had no way to find it.
    const handleOpenTutor = useCallback(async () => {
        await loadCards();
        setShowTutorChat(true);
    }, [loadCards]);

    const openFlashNotes = useCallback(async () => {
        const loaded = await loadCards();
        const f = { ...folder, cards: loaded };
        setFlashNotesAction({ folder: f });
    }, [folder, loadCards]);

    const handleGenerateNotes = useCallback(async (f, action, forceRegenerate = false, _prepaid = false, organizationStyle = 'auto') => {
        if (f.flashNotes && !forceRegenerate) {
            if (action === 'view') {
                setFlashNotesContent({ folderId: f.id, folderName: f.name, notes: f.flashNotes });
                setShowFlashNotesViewer(true);
            }
            return;
        }
        try {
            const includedLeft = Number(entitlements?.includedNotesRemaining ?? 0);
            if (!isDevMode) {
                if (includedLeft <= 0) {
                    pushToast('error', 'Not enough credits for Flash Notes');
                    setFlashNotesAction(null);
                    return;
                }
                await authApi(`${API_BASE_URL}/api/credits/debit`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ feature: 'flashnote', quantity: 1 }),
                });
                if (typeof refreshEntitlements === 'function') {
                    try { await refreshEntitlements(); } catch (_) {}
                }
            }
        } catch (_) {}

        setIsGeneratingNotes(true);
        try {
            const data = await authApiIdempotent(`${API_BASE_URL}/generate-notes`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ cards: f.cards, organizationStyle }),
            });
            const notes = data.notes;
            setFolder(prev => prev ? { ...prev, flashNotes: notes } : prev);
            const uid = getUid();
            const db = getDb();
            if (uid && db) {
                updateDoc(doc(db, 'users', uid, 'folders', f.id), {
                    flashNotes: notes,
                    updatedAt: serverTimestamp(),
                }).catch(() => {});
            }
            if (action === 'view') {
                setFlashNotesContent({ folderId: f.id, folderName: f.name, notes });
                setShowFlashNotesViewer(true);
            }
            pushToast('success', 'Flash Notes generated');
        } catch (e) {
            pushToast('error', e?.message || 'Failed to generate notes');
        } finally {
            setIsGeneratingNotes(false);
        }
    }, [entitlements, isDevMode, refreshEntitlements, pushToast]);

    const handleSaveNotes = useCallback(async (editedNotes) => {
        if (!flashNotesContent?.folderId) return;
        setFolder(prev => prev ? { ...prev, flashNotes: editedNotes } : prev);
        setFlashNotesContent(prev => ({ ...prev, notes: editedNotes }));
        const uid = getUid();
        const db = getDb();
        try {
            if (uid && db) {
                await updateDoc(doc(db, 'users', uid, 'folders', flashNotesContent.folderId), {
                    flashNotes: editedNotes,
                    updatedAt: serverTimestamp(),
                });
            }
        } catch (_) {}
        pushToast('success', 'Notes saved');
    }, [flashNotesContent, pushToast]);

    // ── Render ────────────────────────────────────────────────────────────────

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="h-8 w-8 rounded-full border-2 border-teal-500 border-t-transparent animate-spin" />
            </div>
        );
    }

    if (!folder) {
        return (
            <div className="flex flex-col items-center justify-center min-h-screen gap-4 text-center px-4">
                <p className="text-slate-400">Deck not found.</p>
                <button
                    onClick={() => navigate('/app/home')}
                    className="rounded-xl bg-white/8 border border-white/10 px-4 py-2 text-sm text-slate-200 hover:bg-white/12"
                >
                    Back to Home
                </button>
            </div>
        );
    }

    const anyOverlayOpen = studyingFolder || gameModeFolder || nemesisModeFolder ||
        showGamesModal || flashNotesAction || showFlashNotesViewer;

    return (
        <div className="min-h-screen pb-20">
            {/* ── Breadcrumb nav ──────────────────────────────── */}
            <div className="sticky top-0 z-30 border-b border-white/6 bg-[#0b1220]/80 backdrop-blur">
                <div className="mx-auto max-w-5xl px-4 sm:px-6 h-14 flex items-center gap-3">
                    <button
                        type="button"
                        onClick={() => navigate('/app/home')}
                        className="inline-flex items-center gap-1.5 text-sm text-slate-400 hover:text-white transition"
                    >
                        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                        Home
                    </button>
                    <span className="text-slate-600">/</span>
                    <span className="text-sm text-slate-300 font-medium truncate">{folder.name}</span>
                </div>
            </div>

            <div className="mx-auto max-w-5xl px-4 sm:px-6">
                {/* ── Hero card ─────────────────────────────────── */}
                <div className={`mt-6 rounded-2xl bg-gradient-to-br ${folder.color} p-[1px] shadow-xl`}>
                    <div className="rounded-[15px] bg-[#0b1220]/90 px-6 py-6 sm:px-8 sm:py-7">
                        <div className="flex items-start justify-between gap-4 flex-wrap">
                            <div className="min-w-0">
                                <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-white truncate">
                                    {folder.name}
                                </h1>
                                <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-slate-400">
                                    <span>{folder.cardCount ?? cards.length} cards</span>
                                    {dueCards > 0 && (
                                        <span className="text-amber-400 font-medium">{dueCards} due for review</span>
                                    )}
                                    {folder.updatedAt && (
                                        <span>Updated {timeAgo(folder.updatedAt)}</span>
                                    )}
                                </div>
                            </div>

                            {/* Color swatch */}
                            <div className={`h-12 w-12 shrink-0 rounded-xl bg-gradient-to-br ${folder.color} shadow-lg ring-1 ring-white/15`} />
                        </div>

                        {/* Mastery bar */}
                        {cards.length > 0 && (
                            <div className="mt-5">
                                <div className="flex items-center justify-between mb-1.5">
                                    <span className="text-xs text-slate-500">Mastery</span>
                                    <span className="text-xs font-semibold text-emerald-400 tabular-nums">{masteryPct}%</span>
                                </div>
                                <div className="h-2 rounded-full bg-white/8 overflow-hidden">
                                    <div
                                        className="h-full rounded-full bg-gradient-to-r from-emerald-400 to-teal-400 transition-all duration-700"
                                        style={{ width: `${masteryPct}%` }}
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* ── Action bar ────────────────────────────────── */}
                <div className="mt-6 flex flex-wrap justify-center sm:justify-start gap-3">
                    <ActionBtn
                        icon="▶"
                        label="Study"
                        primary
                        badge={dueCards > 0 ? dueCards : null}
                        onClick={openStudy}
                    />
                    <ActionBtn
                        icon="🎮"
                        label="Games"
                        onClick={openGames}
                        disabled={!isOnline}
                    />
                    <ActionBtn
                        icon="📝"
                        label="Flash Notes"
                        onClick={openFlashNotes}
                        disabled={!isOnline || (!folder.cardCount && !cards.length)}
                    />
                    <ActionBtn
                        icon="📊"
                        label="Exams"
                        onClick={() => navigate('/app/home', { state: { openExam: folderId } })}
                        disabled={!isOnline}
                    />
                    <ActionBtn
                        icon="💬"
                        label="Tutor"
                        onClick={handleOpenTutor}
                        disabled={!isOnline || (!folder.cardCount && !cards.length)}
                    />
                    <div className="relative" ref={ankiMenuRef}>
                        <ActionBtn
                            icon="🗂️"
                            label="Anki"
                            onClick={() => setShowAnkiMenu(v => !v)}
                            disabled={!isOnline}
                        />
                        {showAnkiMenu && (
                            <div className="absolute z-20 mt-2 w-52 left-1/2 -translate-x-1/2 rounded-xl bg-slate-900 border border-white/10 shadow-2xl py-1">
                                <button
                                    type="button"
                                    disabled={isImportingAnki}
                                    onClick={() => { setShowAnkiMenu(false); ankiFileInputRef.current?.click(); }}
                                    className="w-full text-left px-3.5 py-2.5 text-sm text-slate-200 hover:bg-white/5 transition disabled:opacity-50"
                                >
                                    {isImportingAnki ? 'Importing…' : 'Import Anki (.apkg)'}
                                </button>
                                <button
                                    type="button"
                                    disabled={isExportingAnki || (!folder.cardCount && !cards.length)}
                                    onClick={() => { setShowAnkiMenu(false); handleExportAnki(); }}
                                    className="w-full text-left px-3.5 py-2.5 text-sm text-slate-200 hover:bg-white/5 transition disabled:opacity-50"
                                >
                                    {isExportingAnki ? 'Exporting…' : 'Export Anki (.apkg)'}
                                </button>
                            </div>
                        )}
                        <input
                            ref={ankiFileInputRef}
                            type="file"
                            accept=".apkg"
                            className="hidden"
                            onChange={e => { const f = e.target.files?.[0]; if (f) handleImportAnkiFile(f); e.target.value = ''; }}
                        />
                    </div>
                    <div className="relative" ref={documentsMenuRef}>
                        <ActionBtn
                            icon="📄"
                            label={isConvertingDocument ? 'Converting…' : 'Documents'}
                            onClick={() => {
                                const opening = !showDocumentsMenu;
                                setShowDocumentsMenu(opening);
                                if (opening) loadSourceDocuments();
                            }}
                            disabled={!isOnline || isConvertingDocument}
                        />
                        {showDocumentsMenu && (
                            <div className="absolute z-20 mt-2 w-72 left-1/2 -translate-x-1/2 rounded-xl bg-slate-900 border border-white/10 shadow-2xl py-1">
                                <button
                                    type="button"
                                    onClick={() => { setShowDocumentsMenu(false); documentFileInputRef.current?.click(); }}
                                    className="w-full text-left px-3.5 py-2.5 text-sm font-medium text-teal-300 hover:bg-white/5 transition"
                                >
                                    + Upload a document to study
                                </button>
                                {sourceDocuments.length > 0 && (
                                    <div className="border-t border-white/8 mt-1 pt-1 max-h-64 overflow-y-auto">
                                        <p className="px-3.5 py-1 text-[10px] uppercase tracking-wider text-slate-500">Saved documents</p>
                                        {sourceDocuments.map((docItem) => (
                                            <button
                                                key={docItem.id}
                                                type="button"
                                                onClick={() => { setShowDocumentsMenu(false); handleReopenDocument(docItem); }}
                                                className="w-full text-left px-3.5 py-2.5 text-sm text-slate-200 hover:bg-white/5 transition truncate"
                                            >
                                                {docItem.fileName}
                                                {docItem.pageCount ? <span className="text-slate-500"> · {docItem.pageCount}p</span> : null}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                        <input
                            ref={documentFileInputRef}
                            type="file"
                            accept=".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                            className="hidden"
                            onChange={e => { const f = e.target.files?.[0]; if (f) handleNewDocumentFile(f); e.target.value = ''; }}
                        />
                    </div>
                </div>

                {/* ── Recent score banner ───────────────────────── */}
                {mostRecentScore && (
                    <div className="mt-6 rounded-2xl border border-emerald-500/20 bg-emerald-500/8 px-5 py-3 flex items-center justify-between">
                        <span className="text-sm text-emerald-300 font-medium">
                            Last score: <strong>{mostRecentScore.score} pts</strong>
                            {mostRecentScore.level && <span className="ml-2 text-emerald-400/70">({mostRecentScore.level})</span>}
                        </span>
                        <button
                            onClick={() => setMostRecentScore(null)}
                            className="text-emerald-500 hover:text-emerald-300 text-xs"
                        >
                            Dismiss
                        </button>
                    </div>
                )}

                {/* ── Card list ─────────────────────────────────── */}
                <div className="mt-8">
                    <div className="flex items-center justify-between mb-4 gap-4 flex-wrap">
                        <h2 className="text-[17px] font-semibold text-white tracking-tight">
                            Cards
                            <span className="ml-2 text-sm font-normal text-slate-500">
                                ({filteredCards.length}{cardSearch ? ` of ${cards.length}` : ''})
                            </span>
                        </h2>
                        <div className="relative">
                            <input
                                type="search"
                                value={cardSearch}
                                onChange={e => setCardSearch(e.target.value)}
                                placeholder="Search cards…"
                                className="pl-8 pr-3 py-2 rounded-xl bg-slate-800/70 border border-white/10 text-sm placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-teal-500/30 w-52"
                            />
                            <svg className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-500 pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                            </svg>
                        </div>
                    </div>

                    {!cardsLoaded ? (
                        <div className="flex flex-col gap-2">
                            {Array.from({ length: 5 }).map((_, i) => (
                                <div key={i} className="h-14 rounded-xl bg-slate-900/50 animate-pulse" />
                            ))}
                        </div>
                    ) : filteredCards.length === 0 ? (
                        <div className="rounded-xl border border-white/8 bg-slate-900/40 px-5 py-10 text-center">
                            <p className="text-slate-400 text-sm">
                                {cardSearch ? 'No cards match your search.' : 'No cards in this deck yet.'}
                            </p>
                        </div>
                    ) : (
                        <div className="flex flex-col gap-2">
                            {filteredCards.map((card, i) => (
                                <CardItem
                                    key={card.id}
                                    card={card}
                                    index={i}
                                    onSave={handleUpdateCard}
                                    onDelete={handleDeleteCard}
                                    sourceDocuments={sourceDocuments}
                                    onViewSource={handleViewCardSource}
                                />
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* ── Analytics panel ───────────────────────────────── */}
            {cardsLoaded && srsStats && (
                <div className="mx-auto max-w-5xl px-4 sm:px-6 mt-10">
                    <h2 className="text-[17px] font-semibold text-white tracking-tight mb-4">Analytics</h2>

                    {/* SRS breakdown */}
                    <div className="grid grid-cols-3 gap-3 mb-5">
                        {[
                            { label: 'New', count: srsStats.new,      bar: 'bg-slate-500',   text: 'text-slate-300' },
                            { label: 'Learning', count: srsStats.learning, bar: 'bg-amber-500', text: 'text-amber-300' },
                            { label: 'Mastered', count: srsStats.mastered, bar: 'bg-emerald-500', text: 'text-emerald-300' },
                        ].map(({ label, count, bar, text }) => {
                            const pct = srsStats.total > 0 ? Math.round((count / srsStats.total) * 100) : 0;
                            return (
                                <div key={label} className="rounded-2xl border border-white/8 bg-slate-900/60 p-4">
                                    <div className={`text-2xl font-bold tabular-nums ${text}`}>{count}</div>
                                    <div className="text-xs text-slate-500 mt-0.5 mb-2">{label}</div>
                                    <div className="h-1.5 rounded-full bg-white/8 overflow-hidden">
                                        <div className={`h-full rounded-full ${bar} transition-all duration-700`} style={{ width: `${pct}%` }} />
                                    </div>
                                    <div className="text-[10px] text-slate-600 mt-1 tabular-nums">{pct}%</div>
                                </div>
                            );
                        })}
                    </div>

                    {/* Focus mode + hardest cards */}
                    {hardestCards.length > 0 && (
                        <div className="rounded-2xl border border-white/8 bg-slate-900/60 p-5">
                            <div className="flex items-center justify-between mb-3 flex-wrap gap-3">
                                <div>
                                    <h3 className="text-sm font-semibold text-white">Hardest Cards</h3>
                                    <p className="text-xs text-slate-500 mt-0.5">Lowest ease factor — these need the most work</p>
                                </div>
                                <button
                                    onClick={handleFocusStudy}
                                    className="inline-flex items-center gap-1.5 rounded-xl bg-gradient-to-r from-rose-600 to-orange-600 hover:opacity-90 transition px-3.5 py-2 text-xs font-semibold text-white shadow-sm shadow-rose-600/20"
                                >
                                    <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                                    </svg>
                                    Focus Mode
                                </button>
                            </div>
                            <div className="flex flex-col gap-2">
                                {hardestCards.map((c) => (
                                    <div key={c.id} className="flex items-start gap-3 rounded-xl border border-white/6 bg-slate-950/40 px-4 py-2.5">
                                        <span className="mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-rose-500/15 text-rose-400 text-[9px] font-bold">
                                            {Math.round((c.srsEaseFactor ?? 2.5) * 10) / 10}
                                        </span>
                                        <p className="text-xs text-slate-300 leading-snug line-clamp-2">{c.question}</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Focus mode for all-new decks */}
                    {hardestCards.length === 0 && dueCards > 0 && (
                        <div className="rounded-2xl border border-amber-500/20 bg-amber-500/5 p-5 flex items-center justify-between gap-4">
                            <div>
                                <p className="text-sm font-semibold text-amber-300">{dueCards} cards due for review</p>
                                <p className="text-xs text-slate-500 mt-0.5">Study these to keep your streak going</p>
                            </div>
                            <button
                                onClick={handleFocusStudy}
                                className="shrink-0 rounded-xl bg-gradient-to-r from-amber-600 to-orange-600 hover:opacity-90 transition px-4 py-2 text-xs font-semibold text-white"
                            >
                                Study Due Cards
                            </button>
                        </div>
                    )}
                </div>
            )}

            {/* ── Overlays ──────────────────────────────────────── */}

            {studyingFolder && (
                <FlashcardViewer
                    folder={studyingFolder}
                    entitlements={entitlements}
                    isDevMode={isDevMode}
                    onOpenCredits={(payload) => setCreditPrompt(payload)}
                    onClose={(finalDeck) => {
                        const studied = Array.isArray(finalDeck) ? finalDeck.filter(c => c.lastViewed).length : 0;
                        const xpEarned = studied * XP_PER_CARD;
                        if (studied > 0) {
                            addXP(xpEarned);
                            recordStudyToday();
                        }
                        setStudyingFolder(null);
                        if (studied > 0) {
                            setStudyRecap({
                                reviewed: studied,
                                xpEarned,
                                masteryPct,
                            });
                        }
                    }}
                    onLaunchGame={(f) => { setStudyingFolder(null); handleLaunchGame(f); }}
                    onLaunchAnamnesisNemesis={(f) => { setStudyingFolder(null); handleLaunchNemesis(f || studyingFolder); }}
                    onLaunchExam={() => { setStudyingFolder(null); navigate('/app/home', { state: { openExam: folderId } }); }}
                    onCardRated={async (updatedCard) => {
                        try {
                            setCards(prev => prev.map(c => c.id === updatedCard.id ? { ...c, ...updatedCard } : c));
                            setFolder(prev => prev ? {
                                ...prev,
                                cards: (prev.cards || []).map(c => c.id === updatedCard.id ? { ...c, ...updatedCard } : c),
                            } : prev);
                            const uid = getUid();
                            const db = getDb();
                            if (uid && db && folderId) {
                                const { doc: fsDoc, updateDoc: fsUpdate } = await import('firebase/firestore');
                                await fsUpdate(fsDoc(db, 'users', uid, 'folders', folderId, 'cards', String(updatedCard.id)), {
                                    srsInterval: updatedCard.srsInterval,
                                    srsEaseFactor: updatedCard.srsEaseFactor,
                                    srsRepetitions: updatedCard.srsRepetitions,
                                    srsDueDate: updatedCard.srsDueDate,
                                });
                            }
                        } catch (_) {}
                    }}
                />
            )}

            {showGamesModal && (
                <GamesModal
                    folder={showGamesModal}
                    onClose={() => setShowGamesModal(null)}
                    onLaunchGame={handleLaunchGame}
                    onLaunchAnamnesisNemesis={(f) => { setShowGamesModal(null); handleLaunchNemesis(f); }}
                />
            )}

            {creditPrompt && (
                <CreditConversionModal
                    type={creditPrompt.type}
                    folder={creditPrompt.folder}
                    creditsBalance={entitlements?.creditsBalance ?? 0}
                    defaultQuestions={10}
                    onClose={() => setCreditPrompt(null)}
                    onConfirm={async ({ cost }) => {
                        if (creditPrompt.type !== 'games') { setCreditPrompt(null); return; }
                        try {
                            await authApi(`${API_BASE_URL}/api/credits/purchase-included`, {
                                method: 'POST',
                                headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify({ feature: 'game', units: cost }),
                            });
                            if (typeof refreshEntitlements === 'function') { try { await refreshEntitlements(); } catch (_) {} }
                            setCreditPrompt(null);
                            openGames();
                        } catch (e) {
                            pushToast('error', e?.message || 'Payment failed');
                        }
                    }}
                />
            )}

            {gameModeFolder && (
                <GameViewer
                    key={gameModeFolder.id}
                    folder={gameModeFolder}
                    onClose={handleGameEnd}
                    onBackToStudy={(f) => { setGameModeFolder(null); setStudyingFolder(f); }}
                    onExitGame={() => setGameModeFolder(null)}
                    cameFromStudy={false}
                    mostRecentScore={mostRecentScore}
                />
            )}

            {nemesisModeFolder && (
                <NemesisGame
                    folder={nemesisModeFolder}
                    onClose={() => setNemesisModeFolder(null)}
                />
            )}

            {flashNotesAction && (
                <FlashNotesActionModal
                    folder={flashNotesAction.folder}
                    onClose={() => setFlashNotesAction(null)}
                    onGenerate={handleGenerateNotes}
                    isGenerating={isGeneratingNotes}
                />
            )}

            {showFlashNotesViewer && flashNotesContent && (
                <FlashNotesViewer
                    folderName={flashNotesContent.folderName}
                    notes={flashNotesContent.notes}
                    onSave={handleSaveNotes}
                    onClose={() => setShowFlashNotesViewer(false)}
                />
            )}

            {showTutorChat && (
                <FolderChatSidebar
                    folder={{ ...folder, cards }}
                    onClose={() => setShowTutorChat(false)}
                />
            )}

            {showDocumentStudyModal && (documentStudyFile || studyDocument) && (
                <ClinicalDocumentStudyModal
                    file={documentStudyFile}
                    sourceDocument={studyDocument}
                    initialPage={documentStudyInitialPage}
                    folderId={folderId}
                    clinicalSettings={DEFAULT_CLINICAL_SETTINGS}
                    onCardsGenerated={handleDocumentModalCardsGenerated}
                    onDone={handleDocumentModalDone}
                    onClose={handleDocumentModalDone}
                />
            )}

            {studyRecap && (
                <StudyRecapModal
                    folderName={folder?.name}
                    reviewed={studyRecap.reviewed}
                    xpEarned={studyRecap.xpEarned}
                    masteryPct={studyRecap.masteryPct}
                    onStudyAgain={() => { setStudyRecap(null); openStudy(); }}
                    onPlayGame={() => { setStudyRecap(null); openGames(); }}
                    onClose={() => setStudyRecap(null)}
                />
            )}

            <Toast toasts={toasts} />
        </div>
    );
};

export default DeckPage;
